/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { listHelper, lodash, propsUtils, classnames as cx } from '@ba-ui-toolkit/utils';
import Label from '@ba-ui-toolkit/label';
import ProgressIndicator from '@ba-ui-toolkit/progressindicator';
import Menu from '@ba-ui-toolkit/menu';
import ContextMenu from '@ba-ui-toolkit/contextmenu';
import List, { ListItem } from '@ba-ui-toolkit/list';
import SVGIcon from '@ba-ui-toolkit/svgicon';

import styles from './DataView.scss';

const dataViewClassName = styles.dataView.replace(/"/g, '');

const { groupBy, findIndex, toPairs } = lodash;
const { sanitize, getInheritedProps } = propsUtils;

export function toIndexMap(prevData, newData) {
	const map = {};
	newData.forEach((m, index) => {
		map[index] = findIndex(prevData, ({ id }) => id === newData[index].id);
	});

	return map;
}
export default class DataView extends Component {
	static propTypes = {
		...sanitize(List.propTypes, null, 'length', 'itemRenderer'),
		data: PropTypes.array.isRequired,
		itemMenuContent: PropTypes.array,
		loaded: PropTypes.bool,
		grouped: PropTypes.bool,
		scrollToHideDataViewHeader: PropTypes.bool,
		toSelectionMap: PropTypes.bool,
		selectedItems: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
		onSelectionChange: PropTypes.func,
		onItemMenu: PropTypes.func,
		onMenuClick: PropTypes.func,
		dataViewRenderer: PropTypes.func,
		itemsRenderer: PropTypes.func,
		getMoreData: PropTypes.func,
		onActiveChange: PropTypes.func,
		groupBy: PropTypes.func,
		getId: PropTypes.func,
		tileView: PropTypes.bool,
		onWheel: PropTypes.func,
		emptyState: PropTypes.shape({
			icon: PropTypes.object,
			label: PropTypes.string,
		}),
		selectionMode: PropTypes.oneOf(['none', 'disabled', 'single', 'multi']),
	};

	static defaultProps = {
		tileView: false,
		data: [],
		onSelectionChange: () => {},
		getMoreData: () => {},
		onWheel: () => {},
	};

	state = {};

	_itemRenderer = (index, props) => {
		const Item = React.cloneElement(this.props.dataViewRenderer(index, props), {
			// extra props...
		});

		return Item;
	};

	_renderEmptyState = () => {
		const { emptyState } = this.props;
		const { icon, label } = emptyState;

		return (
			<div
				data-tid="ba-data-view__empty-state"
				className={`${dataViewClassName}__empty-state`}
			>
				<div className={`${dataViewClassName}__empty-state-container`}>
					<div className={`${dataViewClassName}__empty-state-icon`}>
						<SVGIcon iconId={icon.id} size="xlarge" />
					</div>
					<label>{label}</label>
				</div>
			</div>
		);
	};
	zip = (inputEntries, zipBy) => {
		const output = {};
		inputEntries.forEach(entry => {
			const [label, data] = entry;
			let newValue;
			const newLabel = zipBy(label);
			if (!output[newLabel]) {
				newValue = [...data];
			} else {
				newValue = [...output[newLabel], ...data];
			}
			output[newLabel] = newValue;
		});

		return toPairs(output);
	};

	_onSelectionChange = event => {
		const {
			onSelectionChange,
			grouped,
			selectionMode,
			data,
			selectedItems,
			toSelectionMap,
		} = this.props;
		const isSingleMode = selectionMode === 'single';
		let selectedItem;
		if (isSingleMode) {
			selectedItem = listHelper.singleSelect(
				event,
				grouped ? this.groupedData : data,
				selectedItems,
				this._getId
			);
			if (toSelectionMap && selectedItem != null) {
				selectedItem = this._toObjectMap({
					[selectedItem]: true,
				});
			}
		} else {
			selectedItem = listHelper.multiSelect(
				event,
				grouped ? this.groupedData : data,
				selectedItems,
				this._getId
			).selectedMap;
		}

		this.setState(
			{
				selectedItem,
			},
			() => {
				onSelectionChange(selectedItem);
			}
		);
	};

	_getId = index =>
		this.props.getId
			? this.props.getId(
					this.props.grouped ? this.groupedData[index] : this.props.data[index],
					index
			  )
			: index;

	_isSelected = index => {
		const id = this._getId(index);
		const { selectionMode, selectedItems, toSelectionMap } = this.props;
		if (toSelectionMap) {
			return !!selectedItems[id];
		}
		const isSingleMode = selectionMode === 'single';
		if (isSingleMode) {
			return selectedItems === id;
		}

		return !!selectedItems[id];
	};
	_getMoreData = () => {
		const { getMoreData, data } = this.props;
		getMoreData([...data].pop());
	};
	_onMenuChange = (name, value) => {
		const { onItemMenu } = this.props;
		onItemMenu(this.state.menuTarget, value);
		this.setState({
			showMenu: false,
		});
	};
	_onActiveChange = ({ index }) => {
		const { onActiveChange, data, grouped } = this.props;
		const activeItem = grouped ? this.groupedData[index] : data[index];
		onActiveChange && onActiveChange(activeItem);
	};
	_toObjectMap(boolMap) {
		const data = this.props.grouped ? this.groupedData : this.props.data;

		return Object.keys(boolMap).reduce((map, id) => {
			if (boolMap[id]) {
				const item = data.find((obj, i) => String(this._getId(i)) === id);
				if (item) {
					map[id] = item;
				}
			}

			return map;
		}, {});
	}
	_renderList = () => {
		const {
			loaded,
			data,
			onItemMenu,
			onMenuClick,
			selectedItems,
			selectionMode,
			grouped,
			itemsRenderer,
			groupBy: _groupBy,
			tileView,
			...others
		} = this.props;
		const listPassthroughProps = getInheritedProps(others, List);

		let dataLength;
		let dataItemRenderer;

		if (grouped && data.length > 0) {
			this.groupedData = [];
			let groupedDataObj;

			if (!_groupBy) {
				groupedDataObj = toPairs(groupBy(data, 'type'));
			} else {
				groupedDataObj = _groupBy(type => toPairs(groupBy(data, type)), this.zip);
			}

			const groupedDataEntries = groupedDataObj;

			groupedDataEntries.forEach(entry => {
				const [label, dataArr] = entry;
				this.groupedData.push({
					gpLabel: label,
				});
				this.groupedData = [...this.groupedData, ...dataArr];
			});
			const indexMap = toIndexMap(data, this.groupedData);

			dataLength = this.groupedData.length;
			dataItemRenderer = (index, props) => {
				const currItem = this.groupedData[index];
				if (currItem.gpLabel) {
					return (
						<ListItem
							{...props}
							active={false}
							selected={false}
							onMenu={null}
							outerClassName={cx('groupedLabel')}
						>
							<Label
								key={`label-${index}`}
								label={currItem.gpLabel}
								type="caption"
								className={`${dataViewClassName}__groupedLabel`}
							/>
						</ListItem>
					);
				}

				return this._itemRenderer(indexMap[index], props);
			};
		} else {
			dataLength = data.length;
			dataItemRenderer = this._itemRenderer;
		}

		const onMenuProps = {};
		if (onItemMenu) {
			onMenuProps.onMenu = (x, y, e) => {
				this.setState({
					showMenu: true,
					clickTarget: e.target,
					menuTarget: this.state.selectedItem,
				});
			};
		} else if (onMenuClick) {
			onMenuProps.onMenu = (x, y, e) => {
				onMenuClick(x, y, e);
			};
		}

		if (loaded) {
			const classNames = cx({
				multiSelecting: selectionMode !== 'single' && Object.keys(selectedItems).length > 1,
				tileView,
			});

			return (
				<Fragment>
					<List
						data-tid="ba-common-data-view__list"
						className={classNames}
						{...sanitize(others, DataView)}
						{...listPassthroughProps}
						listType="simple"
						length={dataLength}
						itemRenderer={dataItemRenderer}
						itemsRenderer={itemsRenderer}
						onSelectionChange={this._onSelectionChange}
						onActiveChange={this._onActiveChange}
						getMoreData={this._getMoreData}
						isSelected={this._isSelected}
						selectionMode={selectionMode}
						onWheel={this.props.onWheel}
						{...onMenuProps}
					/>
					{data.length === 0 && this._renderEmptyState()}
				</Fragment>
			);
		}

		return null;
	};

	render() {
		const { loaded, itemMenuContent } = this.props;

		return (
			<div className={dataViewClassName}>
				{!loaded && (
					<div data-tid="ba-dataview__loader" className={`${dataViewClassName}__loading`}>
						<ProgressIndicator size="large" variant="circle" />
					</div>
				)}
				{this._renderList()}
				{this.state.showMenu && (
					<ContextMenu
						theme
						placement="bottomRight"
						triggerNode={this.state.clickTarget}
						domNodeToAttachTo={document.body}
						onChange={this._onMenuChange}
						onClose={() => this.setState({ showMenu: false })}
					>
						<Menu>
							<Menu.List content={itemMenuContent} name="itemMenu" />
						</Menu>
					</ContextMenu>
				)}
			</div>
		);
	}
}
